import type { TFunction } from 'i18next';
import {
  Box,
  Button,
  Field,
  Flex,
  Grid,
  Input,
  Link,
  NativeSelectField,
  NativeSelectRoot,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiEdit, FiArchive } from 'react-icons/fi';
import {
  HiArrowDownTray,
  HiClipboardDocument,
  HiSignal,
} from 'react-icons/hi2';
import {
  DataTable,
  type Column,
  ToggleButton,
  TableOptions,
  TableContainer,
  RowActionsMenu,
  TableScrollWrapper,
  DrawerComponent,
  Button as AegixButton,
  CardButton,
  SelectInput,
  SectionCard,
  StatusBadge,
  Tooltip,
} from '@themes/aegix';
import { useSourcesHost } from '../context/SourcesHostContext';
import {
  useSourceList,
  useSourceAlertTypes,
  useSourceMutations,
} from '../hooks/useSources';
import { useSearchDebounce } from '../hooks/useSearchDebounce';
import { SourcesScrollArea } from './SourcesScrollArea';
import { SourcesSiteSection } from './SourcesSiteSection';
import {
  SOURCE_TEMPLATES,
  SourceTemplate,
  SourceTemplateField,
  getTemplateApis,
  isRosterTemplate,
  resolveSelectInitialValue,
  templateSelectDefaults,
  isFieldShown as isFieldShownFor,
  intakeConnectionRows,
  type IntakeConnectionRow,
} from '../utils/sourceTemplates';
import { parseVendorApiErrors } from '../utils/parseVendorApiErrors';
import { sortTemplatesBySourceMatch } from '../utils/scoreTemplate';
import CommandTemplateBuilder, {
  CommandTemplate,
} from './CommandTemplateBuilder';
import CleartouchPortalConfig, {
  PortalAlertTypeConfig,
} from './CleartouchPortalConfig';
import type { IVendorCreate, VendorSite } from '../types/vendor';
import type { SourcesSiteSummary } from '../types/host';

type DrawerStep = 'select-template' | 'form';

const CLEARTOUCH_SITE_FIELD_NAMES = new Set([
  'cleartouch_school_code',
  'cleartouch_app_key',
  'cleartouch_device_groups',
]);

// Module-level stable no-op for hosts that don't implement `useSitesList`.
// Used so the page can still call a hook unconditionally and stay
// rules-of-hooks compliant. Always returns the same array reference.
const EMPTY_SITES_LIST: SourcesSiteSummary[] = [];
const noopUseSitesList = (): SourcesSiteSummary[] => EMPTY_SITES_LIST;

const translateTemplate = (
  template: SourceTemplate,
  t: TFunction,
): SourceTemplate => {
  return {
    ...template,
    name: t(template.name, template.name),
    description: t(template.description, template.description),
    fields: template.fields?.map((f) => ({
      ...f,
      label: t(f.label, f.label),
      placeholder: f.placeholder ? t(f.placeholder, f.placeholder) : undefined,
      help: f.help ? t(f.help, f.help) : undefined,
      options: f.options?.map((o) => ({ ...o, label: t(o.label, o.label) })),
      pluginFiles: f.pluginFiles?.map((pf) => ({
        ...pf,
        label: t(pf.label, pf.label),
      })),
    })),
    sections: template.sections?.map((s) => ({
      ...s,
      label: t(s.label, s.label),
      fields: s.fields.map((f) => ({
        ...f,
        label: t(f.label, f.label),
        placeholder: f.placeholder
          ? t(f.placeholder, f.placeholder)
          : undefined,
        help: f.help ? t(f.help, f.help) : undefined,
        options: f.options?.map((o) => ({ ...o, label: t(o.label, o.label) })),
        pluginFiles: f.pluginFiles?.map((pf) => ({
          ...pf,
          label: t(pf.label, pf.label),
        })),
      })),
    })),
  };
};

export interface LastTestResult {
  success: boolean;
  error_class?: string | null;
  detail: string;
  probed_at: string;
}

export const getErrorMessage = (
  errorClass: string | null | undefined,
  serverDetail: string | undefined,
  serverMessage: string | undefined,
  t: TFunction,
): string => {
  switch (errorClass) {
    case 'missing_base_url':
      return t(
        'stream_test_error_missing_base_url',
        'No Base URL is configured for this source. Set it and save, then test again.',
      );
    case 'missing_credentials':
      return t(
        'stream_test_error_missing_credentials',
        'This source is missing required credentials.',
      );
    case 'auth_failed':
      return t(
        'stream_test_error_auth_failed',
        'The provider rejected these credentials. Check the saved credentials for this source. For Verkada, also check that the auth mode matches how the key was issued; for Eagle Eye, re-authorize to get a new refresh token.',
      );
    case 'forbidden':
      return t(
        'stream_test_error_forbidden',
        'The provider accepted the credentials but refused this request.',
      );
    case 'not_found':
      return t(
        'stream_test_error_not_found',
        'The provider returned 404 for the camera list. Check the Base URL is the right regional endpoint for this organization.',
      );
    case 'unreachable':
      return t(
        'stream_test_error_unreachable',
        'Could not reach the provider. Check the Base URL and network egress.',
      );
    case 'timeout':
      return t(
        'stream_test_error_timeout',
        'The provider did not respond in time.',
      );
    case 'upstream_error':
      return t(
        'stream_test_error_upstream_error',
        'The provider returned an unexpected error. The details were logged; a common cause is a Base URL or credential the provider rejected outright.',
      );
    case 'no_cameras':
      return t(
        'stream_test_error_no_cameras',
        'Connected successfully, but the provider returned no cameras for this organization.',
      );
    default:
      return (
        serverDetail ||
        serverMessage ||
        t('connection_failed_desc', 'Could not verify server connectivity.')
      );
  }
};

export const getSuccessMessage = (
  cameraCount: number | null | undefined,
  serverDetail: string | undefined,
  serverMessage: string | undefined,
  t: TFunction,
): string => {
  if (cameraCount !== undefined && cameraCount !== null) {
    return t('stream_test_success_cameras', {
      count: cameraCount,
      defaultValue_one: 'Connected. 1 camera visible.',
      defaultValue_other: `Connected. ${cameraCount} cameras visible.`,
      defaultValue: `Connected. ${cameraCount} camera${cameraCount !== 1 ? 's' : ''} visible.`,
    });
  }
  return (
    serverDetail ||
    serverMessage ||
    t(
      'connection_successful_desc',
      'Successfully verified server connectivity.',
    )
  );
};

const SourcesComponent = () => {
  const { t } = useTranslation();
  const translatedTemplates = useMemo(() => {
    return SOURCE_TEMPLATES.map((tmpl) => translateTemplate(tmpl, t));
  }, [t]);
  // Host adapter — provides site context, permissions, vendor socket updates,
  // and toaster. The lib never reads from the host's Redux store / AuthContext
  // / axios directly.
  const host = useSourcesHost();
  const {
    client,
    useSiteContext,
    usePermissions,
    useVendorSocketUpdate,
    toaster,
  } = host;
  const {
    siteId: schoolId,
    clientId,
    tokenSecret,
    siteRecord: currentSchool,
  } = useSiteContext();
  // Multi-site mode: when the host implements `useSitesList`, render one
  // collapsible <SourcesSiteSection> per site instead of a single flat list.
  // Hosts that don't (AIM today) leave the page in single-site mode driven
  // by `useSiteContext` alone. We always call SOME hook (the host's, or a
  // module-level no-op) so rules-of-hooks stay satisfied; the host adapter
  // is memoized in each app, keeping the chosen identity stable.
  const useSitesList = host.useSitesList ?? noopUseSitesList;
  const sitesList = useSitesList();
  const isMultiSite = sitesList.length > 0;
  const {
    canViewVendor: CanViewVendor,
    canAddVendor: CanAddVendor,
    canChangeVendor: CanChangeVendor,
    canDeleteVendor: CanDeleteVendor,
  } = usePermissions();
  const vendorDetailsBySocket = useVendorSocketUpdate();

  const [offset, setOffset] = useState(0);
  const [isModal, setIsModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('active');
  // Target site for the create/edit drawer in multi-site mode. For Add,
  // seeded from the section the user invoked (or the first site for the
  // top-level Add Source button). For Edit, set to the site the row was
  // loaded under so save writes back to the correct school. In single-site
  // mode it stays null and the `useSiteContext().siteId` fallback is used.
  const [drawerSiteId, setDrawerSiteId] = useState<number | null>(null);

  // Use shared search debounce hook
  const { searchValue, setSearchValue, debouncedQuery, handleSubmit } =
    useSearchDebounce();

  const [sourceDetailsById, setSourceDetailsById] = useState<VendorSite>();
  const [sourceDetails, setSourceDetails] = useState<VendorSite[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<SourceTemplate | null>(null);
  const [drawerStep, setDrawerStep] = useState<DrawerStep>('select-template');
  // Picker search input — filters by template name OR derived API
  // (e.g. "announce" → Cleartouch, 3CX, PDK, Audio Enhancement, FreePBX;
  // "roster" → all roster providers; "stream" → Verkada).
  const [pickerQuery, setPickerQuery] = useState('');
  const [selectedAlertTypes, setSelectedAlertTypes] = useState<number[]>([]);
  const [selectedAnnounceAlertTypes, setSelectedAnnounceAlertTypes] = useState<
    number[]
  >([]);
  const [toggleStates, setToggleStates] = useState<Record<string, boolean>>({});
  // Current value of every select in the active template. Selects are
  // uncontrolled (read from FormData on submit), but `showWhen` has to answer
  // "is this field visible" during render, so the chosen value is tracked here.
  const [selectValues, setSelectValues] = useState<Record<string, string>>({});
  const [commandTemplates, setCommandTemplates] = useState<CommandTemplate[]>(
    [],
  );
  const [portalConfig, setPortalConfig] = useState<PortalAlertTypeConfig[]>([]);

  // Refs to the three Cleartouch site-level inputs, used to imperatively
  // populate them from a parsed Postman file.
  const siteInputRefs = useRef<
    Record<string, HTMLInputElement | HTMLTextAreaElement | null>
  >({});

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Fetch alert types for the drawer's target site — the site chosen in the
  // Add Source selector or, on edit, the row's site — so announce /
  // Cleartouch templates show that site's alert types. Falls back to the
  // host's active `schoolId` when the drawer is closed or in single-site mode.
  const { alertTypes, isLoading: alertTypesLoading } = useSourceAlertTypes(
    drawerSiteId ?? schoolId,
  );

  const {
    sources: sourceResults,
    next: nextUserUrl,
    isFetching: isLoadingSources,
    isLoading: isInitialLoadingSources,
    error: sourceListError,
  } = useSourceList({
    school: schoolId,
    query: debouncedQuery,
    offset: offset,
    limit: 100,
    // 'all' must be sent explicitly — omitting the param means
    // active-only on the backend (the safe default for legacy callers).
    deleted: statusFilter === 'all' ? 'all' : statusFilter === 'archived',
  });

  // Sync fetched results into local sourceDetails state.
  // Always set `isLoaded` once a fetch completes (even when empty) so the
  // page can distinguish "empty result" from "still loading" — without this,
  // a legitimately empty backend response and a never-fetched state look
  // identical in the UI.
  useEffect(() => {
    if (!CanViewVendor) return;
    if (debouncedQuery || offset === 0) {
      setSourceDetails(sourceResults);
    } else if (sourceResults.length > 0) {
      setSourceDetails((prev) => [...prev, ...sourceResults]);
    }
    if (!isLoadingSources) {
      setIsLoaded(true);
    }
  }, [sourceResults, isLoadingSources]);

  // Surface fetch failures as a toast so 4xx/5xx responses don't show as a
  // silent empty list. Only fires once per error transition (key changes
  // when the error object changes).
  useEffect(() => {
    if (sourceListError) {
      const message =
        (sourceListError as any)?.response?.data?.error ||
        (sourceListError as any)?.message ||
        t('failed_to_load_sources');
      toaster.create({
        title: message,
        type: 'error',
        duration: 6000,
        closable: true,
      });
    }
  }, [sourceListError]);
  useEffect(() => {
    if (vendorDetailsBySocket == null) return; // skip if undefined or null
    if (CanViewVendor) {
      setSourceDetails((prev) => {
        // if vendorDetailsBySocket is a "delete" signal, remove it
        if (vendorDetailsBySocket.is_deleted === true) {
          return prev.filter((item) => item.id !== vendorDetailsBySocket.id);
        }
        // normal update/add logic
        const exists = prev.some(
          (item) => item.id === vendorDetailsBySocket.id,
        );
        if (exists) {
          return prev.map((item) =>
            item.id === vendorDetailsBySocket.id
              ? { ...item, ...vendorDetailsBySocket }
              : item,
          );
        } else {
          return [vendorDetailsBySocket, ...prev];
        }
      });
    }
  }, [vendorDetailsBySocket]);

  // Source mutations from extracted hook
  const {
    createSource: createVendorMutation,
    updateSource: updateVendorMutation,
    deleteSource,
  } = useSourceMutations();

  // Wrap deleteSource with custom toast handlers
  const deleteVendorMutation = {
    ...deleteSource,
    mutate: (id: string) => {
      deleteSource.mutate(id, {
        onSuccess: () => {
          toaster.create({
            title: t('source_has_been_removed'),
            type: 'success',
            closable: true,
            duration: 5000,
          });
        },
        onError: (error: any) => {
          toaster.create({
            title: error?.response?.data?.error,
            type: 'error',
            closable: true,
            duration: 5000,
          });
        },
      });
    },
  };

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [lastTestResult, setLastTestResult] = useState<LastTestResult | null>(
    null,
  );
  const [isTestingConnection, setIsTestingConnection] = useState(false);

  const handleTestConnection = async (field?: SourceTemplateField) => {
    if (!client.testSourceConnection) return;
    setIsTestingConnection(true);
    try {
      const vendorId = sourceDetailsById?.id ?? null;
      const sourceCode = field?.testSource || selectedTemplate?.id;
      const schoolIdValue = drawerSiteId ?? schoolId ?? undefined;

      // Save-first architecture (ADR-067-A Amendment 1 §1.1): probe runs strictly
      // against the saved configuration on the backend and accepts no credentials in body.
      const res = await client.testSourceConnection(vendorId, {
        source: sourceCode,
        school: schoolIdValue,
      });

      // 🚨 no_cameras is a FAILURE class (ADR 067-A Amendment 1 §1.2) — never treat as success!
      // Enforce on both error_class AND camera_count === 0 to defend against remote services
      // that return success: true with 0 cameras.
      const noCameras =
        res?.error_class === 'no_cameras' || res?.camera_count === 0;
      const isSuccess = Boolean(res?.success === true && !noCameras);
      const errorClass = res?.error_class ?? (noCameras ? 'no_cameras' : null);

      if (!isSuccess) {
        const detailMsg = getErrorMessage(
          errorClass,
          res?.detail,
          res?.message,
          t,
        );
        setLastTestResult({
          success: false,
          error_class: errorClass,
          detail: detailMsg,
          probed_at: new Date().toISOString(),
        });
        toaster.create({
          title: t('connection_failed', 'Connection test failed'),
          description: detailMsg,
          type: 'error',
          duration: 6000,
          closable: true,
        });
      } else {
        const successMsg = getSuccessMessage(
          res?.camera_count,
          res?.detail,
          res?.message,
          t,
        );
        setLastTestResult({
          success: true,
          error_class: null,
          detail: successMsg,
          probed_at: new Date().toISOString(),
        });
        toaster.create({
          title: t('connection_successful', 'Connection successful'),
          description: successMsg,
          type: 'success',
          duration: 5000,
          closable: true,
        });
      }
    } catch (err: any) {
      const errorClass = err?.data?.error_class || err?.error_class || null;
      const errorMsg = getErrorMessage(
        errorClass,
        err?.data?.detail || err?.detail || err?.response?.data?.detail,
        err?.data?.message || err?.message || err?.response?.data?.message,
        t,
      );
      setLastTestResult({
        success: false,
        error_class: errorClass,
        detail: errorMsg,
        probed_at: new Date().toISOString(),
      });
      toaster.create({
        title: t('connection_failed', 'Connection test failed'),
        description: errorMsg,
        type: 'error',
        duration: 6000,
        closable: true,
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const columns = useMemo<Column<VendorSite>[]>(
    () => [
      { key: 'name', label: t('source_column') },
      { key: 'announce_host', label: t('header_column') },
    ],
    [t],
  );

  // Row-actions factory: the optional `siteId` lets multi-site sections
  // pin the Edit click to the correct school (otherwise the save would
  // write to the drawer's last-seeded site).
  const renderRowActionsFor = (siteId?: number) => (row: VendorSite) => (
    <RowActionsMenu
      actions={[
        {
          label: t('edit'),
          icon: <FiEdit />,
          onClick: () => handleOpenEdit(row, siteId),
          permission: CanChangeVendor,
          isPrimary: true,
          e2eTitle: 'edit',
        },
        {
          label: t('archive'),
          icon: <FiArchive />,
          onClick: () => deleteVendorMutation.mutate(String(row.id)),
          permission: CanDeleteVendor,
          e2eTitle: 'archive',
        },
      ]}
    />
  );
  const renderRowActions = renderRowActionsFor();

  // currentSchool, clientId, and tokenSecret come from the host adapter
  // (`useSiteContext()`); the lib does not derive them from a Redux store.

  const handleCopyClientId = () => {
    if (clientId) {
      navigator.clipboard
        .writeText(clientId)
        .then(() => {
          toaster.create({
            title: t('client_id_copied_to_clipboard'),
            type: 'success',
            duration: 2000,
          });
        })
        .catch(() => {
          toaster.create({
            title: t('failed_to_copy_client_id'),
            type: 'error',
            duration: 2000,
          });
        });
    }
  };

  const handleCopyTokenSecret = () => {
    if (tokenSecret) {
      navigator.clipboard
        .writeText(tokenSecret)
        .then(() => {
          toaster.create({
            title: t('token_secret_copied_to_clipboard'),
            type: 'success',
            duration: 2000,
          });
        })
        .catch(() => {
          toaster.create({
            title: t('failed_to_copy_token_secret'),
            type: 'error',
            duration: 2000,
          });
        });
    }
  };

  // Wrapped so a missing Clipboard API (insecure context) lands in the error
  // toast instead of throwing out of the click handler.
  const handleCopyValue = (value: string, label: string) => {
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(value))
      .then(() => {
        toaster.create({
          title: t('value_copied_to_clipboard', { label }),
          type: 'success',
          duration: 2000,
        });
      })
      .catch(() => {
        toaster.create({
          title: t('failed_to_copy_value', { label }),
          type: 'error',
          duration: 2000,
        });
      });
  };

  // Reset data when schoolId, debouncedQuery, or statusFilter changes
  const isInitialFilterMount = useRef(true);
  useEffect(() => {
    if (isInitialFilterMount.current) {
      isInitialFilterMount.current = false;
      return;
    }
    setSourceDetails([]);
    setOffset(0);
  }, [schoolId, debouncedQuery, statusFilter]);

  const handleAlertTypeToggle = (alertTypeId: number) => {
    setIsFormDirty(true);
    setSelectedAlertTypes((prev) =>
      prev.includes(alertTypeId)
        ? prev.filter((id) => id !== alertTypeId)
        : [...prev, alertTypeId],
    );
  };

  const handleAnnounceAlertTypeToggle = (alertTypeId: number) => {
    setIsFormDirty(true);
    setSelectedAnnounceAlertTypes((prev) =>
      prev.includes(alertTypeId)
        ? prev.filter((id) => id !== alertTypeId)
        : [...prev, alertTypeId],
    );
  };

  /** Populate the three Cleartouch site-level inputs from values parsed out
   *  of a Postman collection. Fill silently when empty; warn (and still
   *  overwrite) when the existing value differs. */
  const handleSiteImport = (site: {
    schoolCode?: string;
    appKey?: string;
    deviceGroups?: string[];
  }) => {
    setIsFormDirty(true);
    const changes: { label: string; field: string; newValue: string }[] = [
      ...(site.schoolCode !== undefined
        ? [
            {
              label: 'School Code',
              field: 'cleartouch_school_code',
              newValue: site.schoolCode,
            },
          ]
        : []),
      ...(site.appKey !== undefined
        ? [
            {
              label: 'App Key',
              field: 'cleartouch_app_key',
              newValue: site.appKey,
            },
          ]
        : []),
      ...(site.deviceGroups !== undefined
        ? [
            {
              label: 'Device Groups',
              field: 'cleartouch_device_groups',
              newValue: site.deviceGroups.join('\n'),
            },
          ]
        : []),
    ];

    const warnings: string[] = [];
    for (const { label, field, newValue } of changes) {
      const el = siteInputRefs.current[field];
      if (!el) continue;
      const current = el.value ?? '';
      if (current === newValue) continue;
      if (current !== '') {
        warnings.push(`${label}: "${current}" → "${newValue}"`);
      }
      el.value = newValue;
    }
    if (warnings.length > 0) {
      toaster.create({
        type: 'warning',
        title: 'Site values updated from Postman file',
        description: warnings.join('\n'),
      });
    }
  };

  // Initialize toggle states from template defaults when a template is selected
  const initToggleStates = (template: SourceTemplate) => {
    const states: Record<string, boolean> = {};
    const registerToggles = (fields: SourceTemplateField[]) => {
      fields.forEach((field) => {
        if (field.type === 'toggle') {
          states[field.name] = field.defaultChecked ?? false;
        }
      });
    };
    if (template.fields) registerToggles(template.fields);
    template.sections?.forEach((s) => registerToggles(s.fields));
    return states;
  };

  // Check if a field should be visible based on its parent toggle
  const isFieldVisible = (parent?: string) => {
    if (!parent) return true;
    return toggleStates[parent] ?? false;
  };

  // Initial value of each select: the stored one when editing, else the
  // template's declared default.
  const initSelectValues = (
    template: SourceTemplate,
    source?: Record<string, any>,
  ) => {
    const values: Record<string, string> = {};
    const visit = (field: SourceTemplateField) => {
      if (field.type !== 'select') return;
      const stored = source ? source[field.name] : undefined;
      values[field.name] =
        resolveSelectInitialValue(
          stored != null ? String(stored) : undefined,
          field,
        ) ?? '';
    };
    template.fields?.forEach(visit);
    template.sections?.forEach((s) => s.fields.forEach(visit));
    return values;
  };

  // Selects fall back to the template's declared default until hydration or
  // the user sets one, so visibility is right on the first render too.
  const isFieldShown = (field: SourceTemplateField) =>
    isFieldShownFor(field, {
      toggles: toggleStates,
      selects: {
        ...(selectedTemplate ? templateSelectDefaults(selectedTemplate) : {}),
        ...selectValues,
      },
    });

  /**
   * Build the API payload generically from the active template.
   *
   * Convention: section.id = API payload key (announce, stream, alert, …).
   * Within a section, stripping the "{sectionId}_" prefix from the field name
   * gives the key inside that nested object.
   * Top-level fields (template.fields) map directly to payload root keys.
   * Special field types (alert_types, command_templates, cleartouch_portal)
   * are handled by type.
   */
  const buildPayload = (
    formData: FormData,
    template: SourceTemplate,
  ): IVendorCreate => {
    const payload: IVendorCreate = {
      // `code` is advisory only — VendorSerializer.to_internal_value derives
      // the stored code server-side: slug-of-name normally, or the canonical
      // stream-provider code when template_id is registered (so display-named
      // stream sources stay routable per ADR-067-A Decision 5).
      code: formData.get('name')?.toString().trim() ?? '',
      name: formData.get('name')?.toString().trim() ?? '',
      // In multi-site mode (SMS), `drawerSiteId` is set to the site the
      // drawer was opened against — either the row's site for edits or
      // the user's chosen "add to" site for new vendors. Falls back to
      // single-site mode's active `schoolId`. The 0-default is unreachable
      // because the drawer can't open without a site.
      school: drawerSiteId ?? schoolId ?? 0,
      // Persist which source template built this vendor so the edit
      // drawer can re-render the correct form. Without this, the edit lookup
      // at handleOpenEdit tries to match source.code (slugified name) to
      // template.id and fails for any non-default name.
      template_id: template.id,
    };

    // Read a form/toggle value for a field
    const readField = (field: SourceTemplateField): any => {
      if (field.type === 'toggle') {
        return toggleStates[field.name] ?? false;
      }
      const raw = formData.get(field.name)?.toString() ?? '';
      if (field.type === 'textarea') {
        return raw
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean);
      }
      return raw.trim() || null;
    };

    // Process top-level fields (flat roster templates: aeries, classlink,
    // facts, …). The sections loop below does not cover them, so they must
    // be serialized here or every flat-field template silently drops its
    // credentials on submit. Emit each as a flat payload key matching the
    // backend's JSONB accessor field names (e.g. roster_login_secret), the
    // wire shape the vendors_vendor bridge expects.
    for (const field of template.fields ?? []) {
      if (
        field.name === 'name' ||
        field.type === 'connection_test' ||
        field.type === 'plugin_download' ||
        field.type === 'intake_connection'
      )
        continue; // extracted above via code/name or non-persisted action
      if (!isFieldShown(field)) continue;
      const value = readField(field);
      // Optional fields left blank are omitted rather than sent as null —
      // nulling would clear a stored credential on edit (the credential
      // masking round-trip relies on absent-or-sentinel, never null).
      if (value === null && !field.required) continue;
      (payload as Record<string, any>)[field.name] = value;
    }

    // Process each section → nested payload object
    for (const section of template.sections ?? []) {
      const sectionObj: Record<string, any> = {};
      const prefix = `${section.id}_`;

      for (const field of section.fields) {
        if (!isFieldShown(field)) continue;
        if (
          field.type === 'connection_test' ||
          field.type === 'plugin_download' ||
          field.type === 'intake_connection'
        )
          continue;
        // Special types go to top-level payload keys
        if (field.type === 'alert_types') {
          // The backend stores ONE shared set of alert-type rows per
          // vendor (VendorAlertTypeAlertTypes) and the viewset only reads
          // `alert_types` — sending `announce_alert_types` for announce-scoped
          // fields would silently never persist announce-only templates'
          // (cleartouch, 3cx, freepbx) selections. Emit the union under
          // `alert_types` regardless of scope, and always emit (even empty)
          // when the enclosing toggle is on so "deselect all" can clear the
          // rows.
          const announceOn = toggleStates['announce_active'];
          const alertOn = toggleStates['alert_active'];
          if (announceOn || alertOn) {
            const union = Array.from(
              new Set([
                ...(announceOn ? selectedAnnounceAlertTypes : []),
                ...(alertOn ? selectedAlertTypes : []),
              ]),
            );
            // Set once; subsequent alert_types fields (templates with both
            // scopes) don't need to overwrite with the same union.
            if (
              !(
                'alert_types' in (payload as unknown as Record<string, unknown>)
              )
            ) {
              payload.alert_types = union;
            }
          }
          continue;
        }

        if (field.type === 'command_templates') {
          // Serialize command templates → announce.options
          const validTemplates = commandTemplates.filter(
            (t) => t.name.trim() && t.fields.some((f) => f.key.trim()),
          );
          if (validTemplates.length > 0) {
            const optionsObj: Record<string, Record<string, string>> = {};
            for (const t of validTemplates) {
              const fields: Record<string, string> = {};
              for (const f of t.fields) {
                if (f.key.trim()) fields[f.key.trim()] = f.value;
              }
              optionsObj[t.name.trim()] = fields;
            }
            sectionObj['options'] = [optionsObj];
          }
          continue;
        }

        if (field.type === 'cleartouch_portal') {
          // Serialize Cleartouch portal config → top-level alert_type_configs
          if (portalConfig.length > 0 && toggleStates['announce_active']) {
            const configs: IVendorCreate['alert_type_configs'] = {};
            for (const cfg of portalConfig) {
              configs[cfg.alertTypeId] = {
                command_portal_enabled: cfg.commandPortalEnabled,
                alert_start_token: cfg.alertStartToken || undefined,
                alert_file_id: cfg.alertFileId || undefined,
                alert_stop_token: cfg.alertStopToken || undefined,
                drill_start_token: cfg.drillStartToken || undefined,
                drill_file_id: cfg.drillFileId || undefined,
                drill_stop_token: cfg.drillStopToken || undefined,
              };
            }
            payload.alert_type_configs = configs;
          }
          continue;
        }

        // Regular fields: strip section prefix → nested key
        const key = field.name.startsWith(prefix)
          ? field.name.slice(prefix.length)
          : field.name;

        // School-level fields (e.g. stream credentials) go to top-level payload
        if (field.storage?.table === 'schools_school') {
          (payload as Record<string, any>)[field.name] = readField(field);
        } else {
          sectionObj[key] = readField(field);
        }
      }

      // Merge with defaults (e.g. client: { active: true } when section has no explicit fields)
      const defaults = (template.defaults as Record<string, any>)?.[section.id];
      (payload as Record<string, any>)[section.id] = {
        ...defaults,
        ...sectionObj,
      };
    }

    // Apply defaults for sections that have no explicit section block
    // (e.g. client, event defaults)
    for (const [key, value] of Object.entries(template.defaults)) {
      if (!(key in (payload as Record<string, any>))) {
        (payload as Record<string, any>)[key] = value;
      }
    }

    return payload;
  };

  /** Collect all renderable fields from the active template */
  const getAllTemplateFields = (
    template: SourceTemplate,
  ): SourceTemplateField[] => {
    const fields: SourceTemplateField[] = [...(template.fields ?? [])];
    for (const section of template.sections ?? []) {
      fields.push(...section.fields);
    }
    return fields;
  };

  const submitVenderDetails = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedTemplate) return;

    const formData = new FormData(e.currentTarget);
    const allFields = getAllTemplateFields(selectedTemplate);
    const urlPattern = /^(https?:\/\/)([\w.-]+)(:[0-9]+)?(\/[\w\-./?%&=]*)?$/i;

    // Validate all fields generically based on their type / required flag
    const errors: Record<string, string> = {};
    for (const field of allFields) {
      // Skip fields the active mode or a parent toggle hides
      if (!isFieldShown(field)) continue;
      // Skip non-input types
      if (
        [
          'toggle',
          'alert_types',
          'command_templates',
          'cleartouch_portal',
          'plugin_download',
          'connection_test',
          'intake_connection',
        ].includes(field.type)
      )
        continue;

      const value = formData.get(field.name)?.toString().trim() || '';

      if (field.required && !value) {
        errors[field.name] = t('field_is_required', { field: field.label });
      } else if (field.type === 'url' && value && !urlPattern.test(value)) {
        errors[field.name] = t('please_enter_a_valid_url');
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});

    const payload = buildPayload(formData, selectedTemplate);

    if (sourceDetailsById?.id) {
      updateVendorMutation.mutate(
        {
          id: String(sourceDetailsById.id),
          data: payload,
        },
        {
          onSuccess: () => {
            setIsModal(false);
            setSourceDetailsById(undefined);
            setIsFormDirty(false);
          },
          onError: (error: any) => {
            const { fieldErrors: mapped, summary } = parseVendorApiErrors(
              error?.response?.data,
            );
            if (Object.keys(mapped).length > 0) {
              setFieldErrors((prev) => ({ ...prev, ...mapped }));
            }
            toaster.create({
              title: summary,
              type: 'error',
              closable: true,
              duration: 5000,
            });
          },
        },
      );
    } else {
      createVendorMutation.mutate(payload, {
        onSuccess: () => {
          setIsModal(false);
          setIsFormDirty(false);
        },
        onError: (error: any) => {
          const { fieldErrors: mapped, summary } = parseVendorApiErrors(
            error?.response?.data,
          );
          if (Object.keys(mapped).length > 0) {
            setFieldErrors((prev) => ({ ...prev, ...mapped }));
          }
          toaster.create({
            title: summary,
            type: 'error',
            closable: true,
            duration: 5000,
          });
        },
      });
    }
  };

  const handleCloseDrawer = () => {
    setIsModal(false);
    setSourceDetailsById(undefined);
    setSelectedTemplate(null);
    setDrawerStep('select-template');
    setPickerQuery('');
    setSelectedAlertTypes([]);
    setSelectedAnnounceAlertTypes([]);
    setToggleStates({});
    setSelectValues({});
    setCommandTemplates([]);
    setPortalConfig([]);
    setFieldErrors({});
    setDrawerSiteId(null);
    setLastTestResult(null);
    setIsFormDirty(false);
  };

  /** Populate drawer state from an existing vendor once we know its
   *  template. Used from `handleOpenEdit` (normal edit path) and from
   *  `handleSelectTemplate` when the user picks a template for a legacy
   *  vendor that didn't resolve via `template_id` or `source.code`. */
  const hydrateFromSource = (template: SourceTemplate, source: VendorSite) => {
    // Toggle states: whatever the source already has.
    const states: Record<string, boolean> = {};
    const hydrateToggles = (fields: SourceTemplateField[]) => {
      fields.forEach((field) => {
        if (field.type === 'toggle') {
          const val = (source as Record<string, any>)[field.name];
          states[field.name] = val === true || val === 'True' || val === 'true';
        }
      });
    };
    if (template.fields) hydrateToggles(template.fields);
    template.sections?.forEach((s) => hydrateToggles(s.fields));
    setToggleStates(states);
    setSelectValues(initSelectValues(template, source as Record<string, any>));

    // Command templates from existing announce_options.
    const opts = source.announce_options;
    if (opts && Array.isArray(opts) && opts.length > 0) {
      const obj = opts[0] as Record<string, Record<string, string>>;
      setCommandTemplates(
        Object.entries(obj).map(([name, fields]) => ({
          name,
          fields: Object.entries(fields).map(([key, value]) => ({
            key,
            value: String(value),
          })),
        })),
      );
    } else {
      setCommandTemplates([]);
    }

    // Alert-type selections. Backend stores a single shared set of
    // VendorAlertTypeAlertTypes rows per vendor; mirror into both scope
    // buckets so UIs that show per-scope pickers stay consistent.
    // The serializer exposes the FK as `alert_type_id`; falling back to
    // `alert_types` (the old, undefined-on-read key) would send nulls.
    const siteAlertTypes = source.vendor_sites?.[0]?.site_alert_types ?? [];
    if (siteAlertTypes.length > 0) {
      const alertIds = siteAlertTypes
        .map((at: any) => at.alert_type_id ?? at.alert_types)
        .filter((id: unknown): id is number => typeof id === 'number');
      setSelectedAlertTypes(alertIds);
      setSelectedAnnounceAlertTypes(alertIds);
    } else {
      setSelectedAlertTypes([]);
      setSelectedAnnounceAlertTypes([]);
    }

    // Cleartouch Command Portal per-alert-type config, from the same rows.
    const hydratedPortal: PortalAlertTypeConfig[] = siteAlertTypes
      .filter(
        (at: any) => typeof (at.alert_type_id ?? at.alert_types) === 'number',
      )
      .map((at: any) => ({
        alertTypeId: at.alert_type_id ?? at.alert_types,
        commandPortalEnabled: Boolean(at.cleartouch_command_portal_enabled),
        alertStartToken: at.cleartouch_start_token_alert ?? '',
        alertFileId:
          at.cleartouch_file_id_alert != null
            ? String(at.cleartouch_file_id_alert)
            : '',
        alertStopToken: at.cleartouch_stop_token_alert ?? '',
        drillStartToken: at.cleartouch_start_token_drill ?? '',
        drillFileId:
          at.cleartouch_file_id_drill != null
            ? String(at.cleartouch_file_id_drill)
            : '',
        drillStopToken: at.cleartouch_stop_token_drill ?? '',
      }));
    const templateUsesPortal = (template.sections ?? []).some((s) =>
      s.fields.some((f) => f.type === 'cleartouch_portal'),
    );
    setPortalConfig(templateUsesPortal ? hydratedPortal : []);
  };

  // Render a single template card in the Add Source picker. Shared by both
  // the "Rosters" and "All Others" columns.
  const renderTemplateCard = (template: SourceTemplate) => (
    <CardButton
      key={template.id}
      onClick={() => handleSelectTemplate(template)}
      description={template.description}
      data-e2e={`sourceTemplate-${template.id}`}
    >
      {template.name}
    </CardButton>
  );

  const handleSelectTemplate = (template: SourceTemplate) => {
    setSelectedTemplate(template);
    setDrawerStep('form');
    setFieldErrors({});
    setIsFormDirty(false);
    if (sourceDetailsById?.id) {
      // Edit-fallback path — a legacy vendor that didn't resolve
      // to a template via `template_id` or `source.code`. User just picked
      // the template manually; hydrate from the vendor's existing data so
      // they don't re-enter everything. Saving will write `template_id`
      // and repair the vendor permanently.
      hydrateFromSource(template, sourceDetailsById);
    } else {
      setSelectedAlertTypes([]);
      setSelectedAnnounceAlertTypes([]);
      setToggleStates(initToggleStates(template));
      setSelectValues(initSelectValues(template));
      setCommandTemplates([]);
      setPortalConfig([]);
    }
  };

  const handleBackToTemplates = () => {
    setSelectedTemplate(null);
    setDrawerStep('select-template');
    setSelectedAlertTypes([]);
    setSelectedAnnounceAlertTypes([]);
    setToggleStates({});
    setSelectValues({});
    setCommandTemplates([]);
    setPortalConfig([]);
    setFieldErrors({});
    setLastTestResult(null);
    setIsFormDirty(false);
  };

  const handleOpenAdd = (siteId?: number) => {
    setSourceDetailsById(undefined);
    setSelectedTemplate(null);
    setDrawerStep('select-template');
    setSelectedAlertTypes([]);
    setSelectedAnnounceAlertTypes([]);
    setToggleStates({});
    setSelectValues({});
    setCommandTemplates([]);
    setPortalConfig([]);
    setLastTestResult(null);
    setIsFormDirty(false);
    // In multi-site mode (SMS) the target site is an explicit choice
    // via the Add Source site selector — don't silently default to the first
    // site (that quietly created sources against the wrong school). Honor an
    // explicit `siteId` if a per-section Add passes one; pre-select only when
    // the org has a single site (no real choice to make); otherwise leave null
    // so the picker is withheld until a site is chosen. Single-site mode stays
    // null and `buildPayload` falls back to schoolId.
    if (isMultiSite) {
      setDrawerSiteId(
        siteId ?? (sitesList.length === 1 ? sitesList[0].id : null),
      );
    }
    setIsModal(true);
  };

  const handleOpenEdit = (source: VendorSite, siteId?: number) => {
    setSourceDetailsById(source);
    setFieldErrors({});
    setLastTestResult(null);
    setIsFormDirty(false);

    // Prefer the persisted template_id. Fall back to source.code for
    // legacy vendors created before template_id was written — that only matches
    // when the vendor happened to be named exactly after the template slug.
    const sourceTemplateId = (source as Record<string, any>)['template_id'];
    const template =
      translatedTemplates.find((t) => t.id === sourceTemplateId) ??
      translatedTemplates.find((t) => t.id === source.code) ??
      null;
    setSelectedTemplate(template);

    if (template) {
      setDrawerStep('form');
      hydrateFromSource(template, source);
    } else {
      // Neither lookup resolved. Rather than render a blank form,
      // show the template picker with vendor context. Saving later writes
      // `template_id` so this fallback runs once per legacy vendor.
      setDrawerStep('select-template');
      setToggleStates({});
      setSelectValues({});
      setSelectedAlertTypes([]);
      setSelectedAnnounceAlertTypes([]);
      setCommandTemplates([]);
      setPortalConfig([]);
    }
    // In multi-site mode, the section that triggered the edit knows which
    // site this row belongs to and passes it in. The save uses that id so
    // the update writes back to the correct school regardless of which
    // section's chrome is active.
    if (isMultiSite) {
      setDrawerSiteId(siteId ?? sitesList[0]?.id ?? null);
    }
    setIsModal(true);
  };

  // Determine drawer title
  const getDrawerTitle = () => {
    if (sourceDetailsById?.id) {
      // Legacy vendor bounced back into template-picker.
      if (drawerStep === 'select-template') {
        return t('pick_template_for_source', { name: sourceDetailsById.name });
      }
      return t('edit_source');
    }
    if (drawerStep === 'select-template') return t('add_source');
    return t('add_template_source', { template: selectedTemplate?.name ?? '' });
  };

  /** Read the existing value for a field from the source being edited.
   *  School-level fields (site config) are read from the current school.
   *  Array-valued fields (e.g. cleartouch_device_groups) are joined with
   *  newlines so they hydrate back into a textarea.
   *
   *  This list holds identifiers and config ONLY — no secrets.
   *  `stream_api_key` is not listed because the API never returns it
   *  (write-only, like the other vendor credentials), so listing it would
   *  promise a hydration that can never happen. Credential inputs render
   *  blank on edit by design; the server treats a blank submit as
   *  "leave unchanged", so saving an untouched form cannot wipe them.
   *
   *  Do NOT add a credential to this list to make its input populate — that
   *  would mean the API is handing the secret back out again. */
  const getEditValue = (fieldName: string): string => {
    if (!sourceDetailsById) return '';
    const schoolFields = [
      'stream_org_id',
      'stream_auth_mode',
      'cleartouch_school_code',
      'cleartouch_app_key',
      'cleartouch_device_groups',
    ];
    if (schoolFields.includes(fieldName) && currentSchool) {
      const val = (currentSchool as Record<string, any>)[fieldName];
      if (Array.isArray(val)) return val.join('\n');
      return val != null ? String(val) : '';
    }
    const val = (sourceDetailsById as Record<string, any>)[fieldName];
    return val != null ? String(val) : '';
  };

  // Render a single field
  const renderField = (field: SourceTemplateField) => {
    // Hide fields the active mode or a parent toggle rules out
    if (!isFieldShown(field)) {
      return null;
    }

    // Compound types that contain their own Switch controls must NOT be
    // wrapped in Field.Root — Chakra v3's Field context would assign the
    // same id to every nested Switch, so only the first would be clickable.
    if (field.type === 'toggle') {
      return (
        <Box key={field.name}>
          <ToggleButton
            title={field.label}
            checked={toggleStates[field.name] ?? false}
            onCheckedChange={(details: { checked: boolean }) => {
              setIsFormDirty(true);
              setToggleStates((prev) => ({
                ...prev,
                [field.name]: details.checked,
              }));
            }}
            e2eTitle={`source-${field.name}`}
          />
          {field.help && (
            <Text color="text.tertiary" fontSize="12px" mt="4px">
              {field.help}
            </Text>
          )}
        </Box>
      );
    }

    if (field.type === 'alert_types') {
      return (
        <Box key={field.name}>
          <Text
            fontSize="14px"
            mb="8px"
            color="text.primary"
            fontWeight="medium"
          >
            {field.label}
          </Text>
          <Box
            border="1px solid"
            borderColor="border.secondary"
            borderRadius="lg"
            maxH="200px"
            overflowY="auto"
            p="8px"
          >
            {alertTypes.length > 0 ? (
              <Grid gap="8px">
                {alertTypes.map((at: any) => {
                  const isAnnounce = field.name === 'announce_alert_types';
                  const selected = isAnnounce
                    ? selectedAnnounceAlertTypes
                    : selectedAlertTypes;
                  const toggle = isAnnounce
                    ? handleAnnounceAlertTypeToggle
                    : handleAlertTypeToggle;
                  return (
                    <ToggleButton
                      key={at.id}
                      name={`${field.name}_${at.id}`}
                      title={at.name}
                      checked={selected.includes(at.id)}
                      onCheckedChange={() => toggle(at.id)}
                      e2eTitle={`source-${field.name}-${at.id}`}
                    />
                  );
                })}
              </Grid>
            ) : alertTypesLoading ? (
              // While the target site's alert types are still loading (e.g. a
              // cold cross-site edit after the selector changed drawerSiteId),
              // don't flash the "none configured" text as if it were a
              // misconfiguration.
              <Text fontSize="13px" color="text.tertiary" p="4px">
                {t('loading_alert_types')}
              </Text>
            ) : (
              <Text fontSize="13px" color="text.tertiary" p="4px">
                {t('no_alert_types_configured_for_this_site')}
              </Text>
            )}
          </Box>
          {field.help && (
            <Text color="text.tertiary" fontSize="12px" mt="4px">
              {field.help}
            </Text>
          )}
        </Box>
      );
    }

    // Cleartouch portal — compound widget, no Field.Root
    if (field.type === 'cleartouch_portal') {
      return (
        <Box key={field.name}>
          <Text
            fontSize="14px"
            mb="8px"
            color="text.primary"
            fontWeight="medium"
          >
            {field.label}
          </Text>
          <CleartouchPortalConfig
            alertTypes={alertTypes.filter((at: any) =>
              selectedAnnounceAlertTypes.includes(at.id),
            )}
            config={portalConfig}
            onChange={(cfg) => {
              setIsFormDirty(true);
              setPortalConfig(cfg);
            }}
            onSiteImport={handleSiteImport}
          />
          {field.help && (
            <Text color="text.tertiary" fontSize="12px" mt="4px">
              {field.help}
            </Text>
          )}
        </Box>
      );
    }

    // Command templates — compound widget, no Field.Root
    if (field.type === 'command_templates') {
      return (
        <Box key={field.name}>
          <Text
            fontSize="14px"
            mb="8px"
            color="text.primary"
            fontWeight="medium"
          >
            {field.label}
          </Text>
          <CommandTemplateBuilder
            templates={commandTemplates}
            onChange={(tmpl) => {
              setIsFormDirty(true);
              setCommandTemplates(tmpl);
            }}
          />
          {field.help && (
            <Text color="text.tertiary" fontSize="12px" mt="4px">
              {field.help}
            </Text>
          )}
        </Box>
      );
    }

    if (field.type === 'connection_test') {
      // Default-deny: the connection test renders only when the client implements
      // a probe AND the template field names a testSource. A template that names
      // none renders nothing.
      if (!client.testSourceConnection || !field.testSource) return null;

      const isSaved = Boolean(sourceDetailsById?.id);
      const requiresSaveFirst = field.saveFirst ?? true;
      const isUnsaved = !isSaved || isFormDirty;

      const testDisabledReason = !CanChangeVendor
        ? t(
            'test_connection_permission_required',
            'You need permission to test the connection.',
          )
        : requiresSaveFirst && isUnsaved
          ? t(
              'test_connection_save_first',
              'Save the configuration first — the probe runs against the saved configuration.',
            )
          : null;

      const buttonContent = (
        <>
          <HiSignal />{' '}
          {isTestingConnection
            ? t('testing_connection', 'Testing…')
            : field.label || t('test_connection', 'Test Connection')}
        </>
      );

      const actionButton = testDisabledReason ? (
        <Tooltip
          content={testDisabledReason}
          positioning={{ placement: 'top-end' }}
        >
          {/* Disabled buttons swallow pointer events; the Box keeps the tooltip trigger hoverable. */}
          <Box display="inline-block">
            <AegixButton
              visual="secondary"
              type="button"
              disabled
              data-e2e="testConnectionButton"
            >
              {buttonContent}
            </AegixButton>
          </Box>
        </Tooltip>
      ) : (
        <AegixButton
          visual="secondary"
          type="button"
          onClick={() => handleTestConnection(field)}
          disabled={isTestingConnection}
          data-e2e="testConnectionButton"
        >
          {buttonContent}
        </AegixButton>
      );

      return (
        <Box key={field.name} my={3}>
          <SectionCard
            title={t('connection_section_title', 'Connection')}
            e2eTitle="source-connection-card"
            action={actionButton}
          >
            <VStack align="stretch" gap={4} px={5} py={5}>
              {field.help && (
                <Text fontSize="sm" color="text.secondary">
                  {field.help}
                </Text>
              )}
              <Box data-e2e="source-last-probe">
                <Text fontSize="sm" fontWeight="600" mb={1}>
                  {t('last_probe_outcome', 'Last probe outcome')}
                </Text>
                {!lastTestResult ? (
                  <Text
                    fontSize="sm"
                    color="text.tertiary"
                    data-e2e="source-last-probe-empty"
                  >
                    {t(
                      'no_probe_outcome_yet',
                      'No probe outcome recorded yet.',
                    )}
                  </Text>
                ) : (
                  <VStack align="stretch" gap={1}>
                    <Flex align="center" gap={3} wrap="wrap">
                      <Box
                        data-e2e={
                          lastTestResult.success
                            ? 'source-last-probe-success'
                            : 'source-last-probe-error'
                        }
                      >
                        <StatusBadge
                          status={lastTestResult.success ? 'success' : 'failed'}
                          label={
                            lastTestResult.success
                              ? t('probe_status_success', 'Success')
                              : t('probe_status_failed', 'Failed')
                          }
                        />
                      </Box>
                      <Text
                        fontSize="sm"
                        color="text.secondary"
                        data-e2e="source-last-probe-timestamp"
                      >
                        {new Date(lastTestResult.probed_at).toLocaleString()}
                      </Text>
                    </Flex>
                    {lastTestResult.detail && (
                      <Text
                        fontSize="sm"
                        color="text.secondary"
                        data-e2e="source-last-probe-message"
                      >
                        {lastTestResult.detail}
                      </Text>
                    )}
                  </VStack>
                )}
              </Box>
            </VStack>
          </SectionCard>
        </Box>
      );
    }

    // Copy-to-vendor values (ADR-149 §4). Computed server-side from the
    // SAVED source, never from form state, so nothing here is submitted.
    if (field.type === 'intake_connection') {
      const connection = sourceDetailsById?.roster_intake_connection;
      const rows = intakeConnectionRows(connection);
      const rowLabel = (row: IntakeConnectionRow) => {
        switch (row.kind) {
          case 'host':
            return t('intake_connection_host');
          case 'port':
            return t('intake_connection_port');
          case 'username':
            return t('intake_connection_username');
          case 'host_key_fingerprint':
            return t('intake_connection_host_key_fingerprint', {
              keyType: row.keyType ?? '',
            });
        }
      };
      return (
        <Box
          key={field.name}
          bg="surface.secondary"
          border="1px solid"
          borderColor="border.secondary"
          borderRadius="lg"
          p={4}
          mb={2}
          data-e2e="source-intake-connection"
        >
          <Text fontSize="14px" fontWeight="medium" color="text.primary" mb={1}>
            {field.label}
          </Text>
          {field.help && (
            <Text fontSize="13px" color="text.secondary" mb={3}>
              {field.help}
            </Text>
          )}
          {!connection ? (
            // Unsaved, or switched to push and not yet saved: the server has
            // not derived anything for this source.
            <Text
              fontSize="sm"
              color="text.tertiary"
              data-e2e="source-intake-connection-save-first"
            >
              {t('intake_connection_save_first')}
            </Text>
          ) : rows.length === 0 ? (
            <Text
              fontSize="sm"
              color="text.tertiary"
              data-e2e="source-intake-connection-not-configured"
            >
              {t('intake_connection_not_configured')}
            </Text>
          ) : (
            <VStack align="stretch" gap={3}>
              {rows.map((row) => {
                const label = rowLabel(row);
                return (
                  <Box
                    key={`${row.kind}-${row.keyType ?? ''}`}
                    data-e2e={`source-intake-${row.kind}`}
                  >
                    <Text fontSize="sm" fontWeight="600" mb={1}>
                      {label}
                    </Text>
                    <Flex gap={2} align="center">
                      {/* No `name`: a read-only value must never reach FormData. */}
                      <Input
                        readOnly
                        value={row.value}
                        aria-label={label}
                        fontFamily="mono"
                        bg="surface.primary"
                        color="text.primary"
                        borderColor="border.secondary"
                        borderRadius="lg"
                        onFocus={(e) => e.currentTarget.select()}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        aria-label={t('copy_value', { label })}
                        title={t('copy_value', { label })}
                        onClick={() => handleCopyValue(row.value, label)}
                        data-e2e={`source-intake-copy-${row.kind}`}
                      >
                        <HiClipboardDocument />
                      </Button>
                    </Flex>
                  </Box>
                );
              })}
            </VStack>
          )}
        </Box>
      );
    }

    // Plugin download — info card with download links
    if (field.type === 'plugin_download') {
      return (
        <Box
          key={field.name}
          bg="surface.secondary"
          border="1px solid"
          borderColor="border.secondary"
          borderRadius="lg"
          p={4}
          mb={2}
        >
          <Text fontSize="14px" fontWeight="medium" color="text.primary" mb={1}>
            {field.label}
          </Text>
          {field.help && (
            <Text fontSize="13px" color="text.secondary" mb={3}>
              {field.help}
            </Text>
          )}
          {field.pluginFiles && (
            <Flex gap={2} flexWrap="wrap">
              {field.pluginFiles.map((file) => (
                <Link
                  key={file.path}
                  href={file.path}
                  download
                  data-e2e={`source-download-${file.path.split('/').pop()}`}
                >
                  <Button
                    size="sm"
                    variant="outline"
                    colorPalette="blue"
                    asChild
                  >
                    <span>
                      <HiArrowDownTray />
                      {file.label}
                    </span>
                  </Button>
                </Link>
              ))}
            </Flex>
          )}
        </Box>
      );
    }

    // Simple field types — safe inside Field.Root
    return (
      <Field.Root key={field.name}>
        {field.type === 'select' ? (
          <>
            <Field.Label fontSize="14px" mb="8px" color="text.primary">
              {field.label}
            </Field.Label>
            <NativeSelectRoot>
              <NativeSelectField
                name={field.name}
                data-e2e={`source-${field.name}`}
                bg="surface.primary"
                color="text.secondary"
                borderColor="border.secondary"
                borderRadius="lg"
                height="36px"
                fontSize="14px"
                // The stored value always wins, so editing a row
                // never re-defaults it. See `resolveSelectInitialValue`.
                defaultValue={resolveSelectInitialValue(
                  getEditValue(field.name),
                  field,
                )}
                onChange={(e) => {
                  // Tracked so `showWhen` can answer during render; the select
                  // itself stays uncontrolled and is read from FormData.
                  setSelectValues((prev) => ({
                    ...prev,
                    [field.name]: e.currentTarget.value,
                  }));
                  setIsFormDirty(true);
                }}
              >
                {field.options?.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </NativeSelectField>
            </NativeSelectRoot>
            {field.help && (
              <Text color="text.tertiary" fontSize="12px" mt="4px">
                {field.help}
              </Text>
            )}
          </>
        ) : field.type === 'textarea' ? (
          <>
            <Field.Label fontSize="14px" mb="8px" color="text.primary">
              {field.label}
            </Field.Label>
            <Textarea
              ref={
                CLEARTOUCH_SITE_FIELD_NAMES.has(field.name)
                  ? (el) => {
                      siteInputRefs.current[field.name] = el;
                    }
                  : undefined
              }
              placeholder={field.placeholder}
              data-e2e={`source-${field.name}`}
              name={field.name}
              borderRadius="lg"
              padding="8px 12px"
              _placeholder={{ opacity: 1, color: 'text.tertiary' }}
              fontSize="14px"
              bg="surface.primary"
              color="text.secondary"
              required={field.required}
              defaultValue={getEditValue(field.name)}
              rows={4}
              onChange={() => setIsFormDirty(true)}
              borderColor={
                fieldErrors[field.name] ? 'red.500' : 'border.secondary'
              }
            />
            {field.help && (
              <Text color="text.tertiary" fontSize="12px" mt="4px">
                {field.help}
              </Text>
            )}
            {fieldErrors[field.name] && (
              <Text color="red.500" fontSize="sm" mt="4px">
                {fieldErrors[field.name]}
              </Text>
            )}
          </>
        ) : (
          <>
            <Field.Label fontSize="14px" mb="8px" color="text.primary">
              {field.label}
            </Field.Label>
            <Input
              ref={
                CLEARTOUCH_SITE_FIELD_NAMES.has(field.name)
                  ? (el) => {
                      siteInputRefs.current[field.name] = el;
                    }
                  : undefined
              }
              type={field.type === 'url' ? 'text' : field.type}
              placeholder={field.placeholder}
              data-e2e={`source-${field.name}`}
              name={field.name}
              borderRadius="lg"
              height="36px"
              padding="8px 12px"
              _placeholder={{
                opacity: 1,
                color: 'text.tertiary',
              }}
              fontSize="14px"
              bg="surface.primary"
              color="text.secondary"
              required={field.required}
              defaultValue={getEditValue(field.name)}
              onChange={() => setIsFormDirty(true)}
              borderColor={
                fieldErrors[field.name] ? 'red.500' : 'border.secondary'
              }
            />
            {field.help && (
              <Text color="text.tertiary" fontSize="12px" mt="4px">
                {field.help}
              </Text>
            )}
            {fieldErrors[field.name] && (
              <Text color="red.500" fontSize="sm" mt="4px">
                {fieldErrors[field.name]}
              </Text>
            )}
          </>
        )}
      </Field.Root>
    );
  };

  // Client ID chip for header
  const clientIdChip = (
    <Flex
      flex={1}
      alignItems="center"
      justifyContent="center"
      gap={2}
      px={3}
      py={2}
      bg="surface.tertiary"
      borderRadius="md"
      border="1px solid"
      borderColor="border.secondary"
      cursor="pointer"
      onClick={handleCopyClientId}
      title={t('click_to_copy_client_id')}
      data-e2e="copyClientId"
      _hover={{ bg: 'surface.input' }}
      transition="background 0.2s"
    >
      <Text
        color="text.secondary"
        fontSize="12px"
        fontWeight={500}
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
      >
        {t('client_id_colon', { clientId: clientId || t('not_found') })}
      </Text>
      <HiClipboardDocument
        size={16}
        color="var(--chakra-colors-text-secondary)"
      />
    </Flex>
  );

  // Token Secret chip for header
  const tokenSecretChip = (
    <Flex
      flex={1}
      alignItems="center"
      justifyContent="center"
      gap={2}
      px={3}
      py={2}
      bg="surface.tertiary"
      borderRadius="md"
      border="1px solid"
      borderColor="border.secondary"
      cursor="pointer"
      onClick={handleCopyTokenSecret}
      title={t('click_to_copy_token_secret')}
      data-e2e="copyTokenSecret"
      _hover={{ bg: 'surface.input' }}
      transition="background 0.2s"
    >
      <Text
        color="text.secondary"
        fontSize="12px"
        fontWeight={500}
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
      >
        {t('token_secret_colon', {
          tokenSecret: tokenSecret ? '••••••••' : t('not_configured'),
        })}
      </Text>
      <HiClipboardDocument
        size={16}
        color="var(--chakra-colors-text-secondary)"
      />
    </Flex>
  );

  // Allow the picker to show during Edit when the vendor's
  // template couldn't be resolved — same state, added render case.
  const isSelectingTemplate = drawerStep === 'select-template';
  const isPickingForEdit =
    Boolean(sourceDetailsById?.id) && isSelectingTemplate;
  const pickerTemplates =
    isPickingForEdit && sourceDetailsById
      ? sortTemplatesBySourceMatch(translatedTemplates, sourceDetailsById)
      : translatedTemplates;

  // In multi-site mode the user must pick which site a NEW source is
  // added to. The selector renders at the top of the template-picker step, and
  // the picker (search + cards) is withheld until a site is chosen, so a source
  // can't be created against an unintended site. Edit keeps the row's site, so
  // the selector is add-only (`!sourceDetailsById`).
  const siteOptions = useMemo(
    () => sitesList.map((s) => ({ label: s.name, value: String(s.id) })),
    [sitesList],
  );
  // Only force a choice when there's a real one to make. A single-site org is
  // still in "multi-site mode" (isMultiSite = sitesList.length > 0), but
  // handleOpenAdd auto-selects its lone site, so a one-option dropdown would
  // add nothing — require >1 site for the selector to render.
  const needsSiteSelection =
    isMultiSite && !sourceDetailsById && sitesList.length > 1;
  const siteChosen = drawerSiteId != null;
  // Gate the template picker (search + card grid) on having a target site.
  // Always shown when no selector is needed (edit, single-site, AIM).
  const showTemplatePicker = !needsSiteSelection || siteChosen;

  // Filter the picker by name OR derived API. A template matches when the
  // (case-insensitive) query is a substring of its `name`, its `description`,
  // its `id`, or any of its derived APIs (announce / stream / alert / event /
  // client / roster). Empty query => all templates pass.
  const pickerQueryLower = pickerQuery.trim().toLowerCase();
  const matchesPickerQuery = (t: SourceTemplate): boolean => {
    if (!pickerQueryLower) return true;
    if (t.name.toLowerCase().includes(pickerQueryLower)) return true;
    if (t.id.toLowerCase().includes(pickerQueryLower)) return true;
    if (
      typeof t.description === 'string' &&
      t.description.toLowerCase().includes(pickerQueryLower)
    ) {
      return true;
    }
    return getTemplateApis(t).some((api) =>
      api.toLowerCase().includes(pickerQueryLower),
    );
  };
  const filteredPickerTemplates = pickerTemplates.filter(matchesPickerQuery);
  const rosterTemplates = filteredPickerTemplates.filter(isRosterTemplate);
  const otherTemplates = filteredPickerTemplates.filter(
    (t) => !isRosterTemplate(t),
  );

  return (
    <>
      <Flex gap={2} px={4} py={3}>
        {clientIdChip}
        {tokenSecretChip}
      </Flex>

      <TableContainer e2eTitle="sourcesTable">
        <TableOptions
          toggleOptions={[
            { value: 'all', label: t('common.all', t('all', 'All')) },
            {
              value: 'active',
              label: t('common.active', t('active', 'Active')),
            },
            {
              value: 'archived',
              label: t('common.archived', t('archived', 'Archived')),
            },
          ]}
          activeToggle={statusFilter}
          onToggleChange={setStatusFilter}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onSearchSubmit={handleSubmit}
          searchPlaceholder={t('common.searchPlaceholder', 'Search...')}
          primaryAction={
            CanAddVendor
              ? {
                  label: t('add_source'),
                  onClick: () => handleOpenAdd(),
                }
              : undefined
          }
        />

        {isMultiSite ? (
          // Multi-site mode: one collapsible section per site. Each section
          // owns its own scoped useSourceList query; the shared search box
          // and status filter above flow into all of them. Add/Edit
          // mutations invalidate sourceQueryKeys.all so every section
          // refreshes after a write.
          <Box px={4} pb={5}>
            {sitesList.map((site) => (
              <SourcesSiteSection
                key={site.id}
                site={site}
                debouncedQuery={debouncedQuery}
                statusFilter={statusFilter as 'all' | 'active' | 'archived'}
                columns={columns}
                renderRowActions={renderRowActionsFor(site.id)}
                onRowClick={(row) => handleOpenEdit(row, site.id)}
              />
            ))}
          </Box>
        ) : (
          <SourcesScrollArea
            isLoaded={isLoaded}
            isLoading={isLoadingSources}
            offset={offset}
            limit={20}
            setOffset={setOffset}
            nextUrl={nextUserUrl}
          >
            <TableScrollWrapper minWidth="600px">
              <DataTable
                columns={columns}
                data={sourceDetails}
                keyExtractor={(row) => String(row.id)}
                actionsPosition="start"
                renderRowActions={renderRowActions}
                onRowClick={(row) => handleOpenEdit(row)}
              />
              {/* Explicit empty state — distinguishes a completed fetch with
                  no results from a never-fetched state. Without this, a clean
                  empty backend response and a stalled fetch look identical. */}
              {!isInitialLoadingSources &&
                !isLoadingSources &&
                sourceDetails.length === 0 &&
                !sourceListError && (
                  <Flex direction="column" align="center" gap={2} py={10}>
                    <Text fontSize="md" color="text.secondary">
                      {t('no_sources_for_this_site')}
                    </Text>
                    {CanAddVendor && (
                      <Text fontSize="sm" color="text.tertiary">
                        {t(
                          'click_add_source_above_to_configure_your_first_integration',
                        )}
                      </Text>
                    )}
                  </Flex>
                )}
            </TableScrollWrapper>
          </SourcesScrollArea>
        )}
      </TableContainer>

      <DrawerComponent
        isOpen={isModal}
        onClose={handleCloseDrawer}
        title={getDrawerTitle()}
        e2eTitle="sourceDrawer"
        size="lg"
        onBack={
          !sourceDetailsById?.id && drawerStep === 'form'
            ? handleBackToTemplates
            : undefined
        }
        footer={
          !isSelectingTemplate ? (
            <Flex gap={4} width="100%">
              <AegixButton
                visual="secondary"
                data-e2e="sourceCancel"
                onClick={handleCloseDrawer}
                type="button"
                flex={1}
              >
                {t('cancel')}
              </AegixButton>
              <AegixButton
                visual="primary"
                data-e2e="sourceSave"
                type="submit"
                form="sourceDrawerForm"
                flex={1}
              >
                {sourceDetailsById?.id ? t('update') : t('save')}
              </AegixButton>
            </Flex>
          ) : undefined
        }
      >
        {isSelectingTemplate ? (
          <>
            {isPickingForEdit && (
              <Box
                p="12px"
                mb="12px"
                bg="surface.tertiary"
                border="1px solid"
                borderColor="border.secondary"
                borderRadius="lg"
                data-e2e="sourceTemplateFallbackBanner"
              >
                <Text
                  fontSize="14px"
                  color="text.primary"
                  fontWeight={600}
                  mb="4px"
                >
                  {t('confirm_the_template_for_this_source')}
                </Text>
                <Text fontSize="12px" color="text.secondary">
                  {t('we_cant_tell_which_template_built_source', {
                    name: sourceDetailsById?.name ?? '',
                  })}
                </Text>
              </Box>
            )}
            {needsSiteSelection && (
              <Box mb="16px">
                <SelectInput
                  label={t('site')}
                  name="source-site"
                  placeholder={t('select_a_site')}
                  value={drawerSiteId != null ? String(drawerSiteId) : ''}
                  option={siteOptions}
                  onChange={(val: string) => setDrawerSiteId(Number(val))}
                  e2eTitle="source-site-select"
                />
                {!siteChosen && (
                  <Text fontSize="12px" color="text.tertiary" mt="4px">
                    {t('select_a_site_to_choose_a_source_template')}
                  </Text>
                )}
              </Box>
            )}
            {showTemplatePicker && (
              <>
                <Box mb="12px">
                  <Input
                    value={pickerQuery}
                    onChange={(e) => setPickerQuery(e.target.value)}
                    placeholder={t('search_templates_placeholder')}
                    size="sm"
                    data-e2e="sourceTemplateSearch"
                    autoComplete="off"
                  />
                </Box>
                <Grid
                  templateColumns={{ base: '1fr', md: '1fr 1fr' }}
                  gap="16px"
                  alignItems="start"
                >
                  {/* Left column: Rosters */}
                  <Box>
                    <Text
                      fontSize="13px"
                      fontWeight={700}
                      color="text.secondary"
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                      mb="8px"
                      data-e2e="sourceTemplateGroup-rosters"
                    >
                      {t('rosters')}
                    </Text>
                    {rosterTemplates.length === 0 ? (
                      <Text fontSize="12px" color="text.tertiary" py="8px">
                        {pickerQueryLower
                          ? t('no_matches')
                          : t('no_roster_templates_available')}
                      </Text>
                    ) : (
                      <Flex direction="column" gap="12px">
                        {rosterTemplates.map((template) =>
                          renderTemplateCard(template),
                        )}
                      </Flex>
                    )}
                  </Box>
                  {/* Right column: All Others */}
                  <Box>
                    <Text
                      fontSize="13px"
                      fontWeight={700}
                      color="text.secondary"
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                      mb="8px"
                      data-e2e="sourceTemplateGroup-others"
                    >
                      {t('all_others')}
                    </Text>
                    {otherTemplates.length === 0 ? (
                      <Text fontSize="12px" color="text.tertiary" py="8px">
                        {pickerQueryLower
                          ? t('no_matches')
                          : t('no_templates_available')}
                      </Text>
                    ) : (
                      <Flex direction="column" gap="12px">
                        {otherTemplates.map((template) =>
                          renderTemplateCard(template),
                        )}
                      </Flex>
                    )}
                  </Box>
                </Grid>
              </>
            )}
          </>
        ) : (
          // The form's Save button lives in the drawer footer (rendered by
          // DrawerComponent above) and submits via `form="sourceDrawerForm"`.
          // That's how the footer can sit outside the form element while still
          // submitting it.
          <Grid
            as="form"
            id="sourceDrawerForm"
            onChange={() => setIsFormDirty(true)}
            onInput={() => setIsFormDirty(true)}
            onSubmit={
              submitVenderDetails as unknown as React.FormEventHandler<HTMLDivElement>
            }
            gap="20px"
          >
            {/* Top-level fields (no section) — includes name */}
            {(selectedTemplate?.fields ?? []).map((field) =>
              renderField(field),
            )}

            {/* Sections with labelled borders */}
            {selectedTemplate?.sections?.map((section) => (
              <Box
                key={`section-${section.id}`}
                border="1px solid"
                borderColor="border.secondary"
                borderRadius="lg"
                p="16px"
                data-e2e={`source-section-${section.id}`}
              >
                <Text
                  fontSize="13px"
                  fontWeight={600}
                  color="text.secondary"
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                  mb="16px"
                >
                  {section.label}
                </Text>
                <Grid gap="16px">
                  {section.fields.map((field) => renderField(field))}
                </Grid>
              </Box>
            ))}
          </Grid>
        )}
      </DrawerComponent>
    </>
  );
};

export { SourcesComponent as SourcesPage };
export default SourcesComponent;
